import type { SiteSeverity } from "@/services";

/*
 * 등급 팔레트는 Tailwind 램프를 그대로 쓴다. 손으로 고른 값은 근거가 남지 않아
 * 다음 사람이 못 고친다. 색상군은 이벤트 칩(EVENT_SEVERITY_STYLES)과 같다 —
 * 정상 green · 관심 blue · 주의 yellow · 경고 orange · 위험 red · 불확실 slate.
 *
 * 역할별로 램프의 단만 다르다.
 */

/** 500 단. 지도 핀·클러스터 채움, 점·강조선. `bg-green-500` 같은 유틸리티와 같은 값이다. */
export const SEVERITY_COLORS: Record<SiteSeverity, string> = {
  NORMAL: "#00C950",
  ATTENTION: "#2B7FFF",
  CAUTION: "#F0B100",
  WARNING: "#FF6900",
  CRITICAL: "#FB2C36",
  UNCERTAIN: "#62748E",
};

/** 700 단. 흰 배경 위 글자 — 여섯 등급 전부 4.9:1 이상. */
export const SEVERITY_TEXT_COLORS: Record<SiteSeverity, string> = {
  NORMAL: "#008236",
  ATTENTION: "#1447E6",
  CAUTION: "#A65F00",
  WARNING: "#CA3500",
  CRITICAL: "#C10007",
  UNCERTAIN: "#314158",
};

/** 300 단. 미니맵 점 등 옅은 배경이 필요한 곳. */
export const SEVERITY_MAP_COLORS: Record<SiteSeverity, string> = {
  NORMAL: "#7BF1A8",
  ATTENTION: "#8EC5FF",
  CAUTION: "#FFDF20",
  WARNING: "#FFB86A",
  CRITICAL: "#FFA2A2",
  UNCERTAIN: "#CAD5E2",
};

export const SEVERITY_LABELS: Record<SiteSeverity, string> = {
  NORMAL: "정상",
  ATTENTION: "관심",
  CAUTION: "주의",
  WARNING: "경고",
  CRITICAL: "위험",
  UNCERTAIN: "불확실",
};

export const SEVERITY_ORDER: SiteSeverity[] = [
  "NORMAL",
  "ATTENTION",
  "CAUTION",
  "WARNING",
  "CRITICAL",
];

const RANK: Record<SiteSeverity, number> = {
  NORMAL: 0,
  UNCERTAIN: 1,
  ATTENTION: 2,
  CAUTION: 3,
  WARNING: 4,
  CRITICAL: 5,
};

export function shouldPulse(severity: SiteSeverity): boolean {
  return RANK[severity] >= RANK.CAUTION;
}

export function worstSeverity(severities: SiteSeverity[]): SiteSeverity {
  return severities.reduce<SiteSeverity>(
    (worst, cur) => (RANK[cur] > RANK[worst] ? cur : worst),
    "NORMAL"
  );
}

export function severityColor(severity: SiteSeverity | null | undefined): string {
  return SEVERITY_COLORS[severity ?? "NORMAL"] ?? SEVERITY_COLORS.NORMAL;
}

export function severityTextColor(severity: SiteSeverity | null | undefined): string {
  return SEVERITY_TEXT_COLORS[severity ?? "NORMAL"] ?? SEVERITY_TEXT_COLORS.NORMAL;
}

export const PIN = {
  path: "M14.5 1C20.8513 1 26 6.22371 26 12.667C25.9999 15.9322 24.676 18.8824 22.5449 21H22.5479L14.5 31L6.50391 21.0469C4.34468 18.9261 3.00011 15.9567 3 12.667C3 6.22374 8.14872 1.00005 14.5 1Z",
  width: 30,
  height: 31,
  anchorX: 14.5,
  anchorY: 12.5,
  innerRadius: 6.5,
} as const;

export const PIN_ICON_SIZE: [number, number] = [PIN.width, PIN.height];
export const PIN_ICON_ANCHOR: [number, number] = [PIN.width / 2, PIN.height];

export function severityMapColor(s: SiteSeverity): string {
  return SEVERITY_MAP_COLORS[s] ?? SEVERITY_MAP_COLORS.NORMAL;
}

export function createPoiHtml(opts: {
  severity: SiteSeverity;
  label?: string;
  selected?: boolean;
  scale?: number;
  spawnStyle?: string;
}): string {
  // 클러스터와 같은 색을 쓴다. 핀만 따로 두면 현장 하나일 때와 둘이 묶였을 때
  // 같은 등급이 다른 색으로 보인다.
  const color = severityColor(opts.severity);
  const scale = opts.scale ?? 1;
  const w = Math.round(PIN.width * scale);
  const h = Math.round(PIN.height * scale);
  const pulse = shouldPulse(opts.severity)
    ? `<span class="safers-pin__pulse"></span><span class="safers-pin__pulse safers-pin__pulse--delayed"></span>`
    : "";
  const label = opts.label
    ? `<span class="safers-pin__label">${escapeHtml(opts.label)}</span>`
    : "";

  return `<span class="safers-pin${opts.selected ? " is-selected" : ""}${opts.spawnStyle ? " is-spawning" : ""}" style="--poi-color:${color};${opts.spawnStyle ?? ""}">
    ${pulse}
    <svg class="safers-pin__svg" width="${w}" height="${h}" viewBox="0 0 ${PIN.width} ${PIN.height}" aria-hidden="true">
      <path d="${PIN.path}" fill="${color}"/>
      <circle cx="${PIN.anchorX}" cy="${PIN.anchorY}" r="${PIN.innerRadius}" fill="#fff"/>
    </svg>
    ${label}
  </span>`;
}

export function pinIconBox(scale: number): {
  size: [number, number];
  anchor: [number, number];
} {
  const w = Math.round(PIN.width * scale);
  const h = Math.round(PIN.height * scale);
  return { size: [w, h], anchor: [w / 2, h] };
}

export function createDotHtml(opts: { severity: SiteSeverity; selected?: boolean }): string {
  const color = severityMapColor(opts.severity);
  const pulse = shouldPulse(opts.severity) ? `<span class="safers-dot__pulse"></span>` : "";
  return `<span class="safers-dot${opts.selected ? " is-selected" : ""}" style="--poi-color:${color}">${pulse}<span class="safers-dot__core"></span></span>`;
}

export const DOT_ICON_SIZE: [number, number] = [14, 14];
export const DOT_ICON_ANCHOR: [number, number] = [7, 7];

export function createClusterHtml(opts: { count: number; severity: SiteSeverity }): string {
  const color = severityColor(opts.severity);
  const pulse = shouldPulse(opts.severity) ? `<span class="safers-cluster__pulse"></span>` : "";
  return `<span class="safers-cluster" style="--poi-color:${color}">
    ${pulse}
    <span class="safers-cluster__ring"></span>
    <span class="safers-cluster__body">${opts.count}</span>
  </span>`;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!
  );
}
